import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { UserCircle, KeyRound, Upload } from "lucide-react";

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "U";
}

async function fileToDataUrl(file) {
  return await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function Profile() {
  const qc = useQueryClient();
  const { setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceMode = searchParams.get("force") === "1";
  const fileRef = useRef();
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => (await api.get("/profile")).data,
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", designation: "", picture: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  useEffect(() => {
    if (profile) setForm({
      name: profile.name || "",
      email: profile.email || "",
      phone: profile.phone || "",
      designation: profile.designation || "",
      picture: profile.picture || "",
    });
  }, [profile]);

  const saveMut = useMutation({
    mutationFn: (payload) => api.patch("/profile", payload),
    onSuccess: async ({ data }) => {
      toast.success("Profile updated");
      qc.invalidateQueries({ queryKey: ["profile"] });
      // refresh AuthContext so header shows the new name/email/picture
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("[profile] /auth/me refresh failed", err);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update profile"),
  });

  const pwMut = useMutation({
    mutationFn: (payload) => api.post("/profile/change-password", payload),
    onSuccess: async () => {
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
      // M1-13 fix: after the flag is cleared server-side, refresh AuthContext so
      // `must_change_password` becomes false in memory. Then strip the ?force=1
      // query param so the banner dismisses immediately.
      await refresh();
      if (forceMode) {
        navigate("/app/profile", { replace: true });
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to change password"),
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) {
      toast.error("Please choose an image ≤ 400 KB, or paste an https URL instead.");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setForm((f) => ({ ...f, picture: dataUrl }));
  };

  const submitProfile = (e) => {
    e.preventDefault();
    const payload = {};
    for (const k of ["name", "email", "phone", "designation", "picture"]) {
      if (form[k] !== (profile?.[k] || "")) payload[k] = form[k];
    }
    if (!Object.keys(payload).length) {
      toast.info("No changes to save");
      return;
    }
    saveMut.mutate(payload);
  };

  const submitPw = (e) => {
    e.preventDefault();
    if (pw.next.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw.next !== pw.confirm) return toast.error("Passwords do not match.");
    pwMut.mutate({ current_password: pw.current, new_password: pw.next });
  };

  if (!profile) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl space-y-8" data-testid="profile-page">
      {forceMode && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-4 flex items-start gap-3" data-testid="force-change-banner">
          <div className="w-8 h-8 rounded bg-amber-100 grid place-items-center shrink-0">
            <span className="text-amber-700 font-bold">!</span>
          </div>
          <div className="text-sm text-amber-900">
            <div className="font-semibold">First login — please change your password</div>
            <div className="mt-1 text-xs text-amber-800">You'll be able to access the rest of the ERP once you set a new password below.</div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md border border-zinc-200 grid place-items-center bg-white">
          <UserCircle className="w-4 h-4 text-zinc-700" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">My Profile</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Manage your identity, profile picture and password.</p>
        </div>
      </div>

      <form onSubmit={submitProfile} className="border border-zinc-200 rounded-lg bg-white p-6 space-y-5" data-testid="profile-form">
        <div className="flex items-start gap-6">
          <div className="flex flex-col items-center gap-3">
            <Avatar className="w-20 h-20 border border-zinc-200">
              {form.picture && <AvatarImage src={form.picture} alt={form.name} />}
              <AvatarFallback className="bg-zinc-900 text-white font-heading text-lg">{initials(form.name)}</AvatarFallback>
            </Avatar>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="profile-file-input"
            />
            <Button
              type="button" variant="outline" size="sm"
              onClick={() => fileRef.current?.click()}
              className="gap-1.5"
              data-testid="profile-upload-btn"
            ><Upload className="w-3.5 h-3.5" /> Upload photo</Button>
            {form.picture && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, picture: "" }))} className="text-[11px] text-zinc-500 hover:text-red-600" data-testid="profile-remove-photo">
                Remove
              </button>
            )}
          </div>
          <div className="flex-1 grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="p-name">Name</Label>
              <Input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="profile-name" />
            </div>
            <div>
              <Label htmlFor="p-email">Email</Label>
              <Input
                id="p-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={profile.role !== "managing_director" && profile.role !== "super_admin"}
                data-testid="profile-email"
              />
              {profile.role !== "managing_director" && (
                <p className="text-[10px] text-zinc-500 mt-1">Only the Managing Director can change email addresses.</p>
              )}
            </div>
            <div>
              <Label htmlFor="p-des">Designation</Label>
              <Input id="p-des" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="profile-designation" />
            </div>
            <div>
              <Label htmlFor="p-phone">Phone</Label>
              <Input id="p-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
            </div>
            <div className="md:col-span-2 text-[11px] text-zinc-500 font-mono flex items-center gap-4">
              <span>Role: <span className="text-zinc-800">{roleLabel(profile.role)}</span></span>
              <span>User ID: <span className="text-zinc-800">{profile.user_id?.slice(0, 16)}…</span></span>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saveMut.isPending} data-testid="profile-save">
            {saveMut.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>

      <form onSubmit={submitPw} className="border border-zinc-200 rounded-lg bg-white p-6 space-y-5" data-testid="password-form">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-zinc-500" />
          <h2 className="font-heading text-lg font-bold">Change password</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="pw-cur">Current password</Label>
            <Input id="pw-cur" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required data-testid="pw-current" />
          </div>
          <div>
            <Label htmlFor="pw-new">New password</Label>
            <Input id="pw-new" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required minLength={6} data-testid="pw-new" />
          </div>
          <div>
            <Label htmlFor="pw-conf">Confirm new password</Label>
            <Input id="pw-conf" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required minLength={6} data-testid="pw-confirm" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={pwMut.isPending} data-testid="pw-submit">
            {pwMut.isPending ? "Updating…" : "Update password"}
          </Button>
        </div>
      </form>
    </div>
  );
}
